/**
 * Viewport-aware positioning for portal-mounted, `position: fixed` popovers.
 *
 * Every popover in this codebase is portalled to `document.body` and
 * positioned from the trigger's `getBoundingClientRect()` — the chips live
 * inside nested `overflow-auto` scroll containers, so an absolutely
 * positioned popover would clip against the nearest scrollport instead of
 * the viewport. That's the right call; what was missing is the arithmetic
 * to keep the box on screen once it's out there.
 *
 * Two things about `position: fixed` that make the naive version wrong:
 *
 * - Setting `left` while leaving `right: auto` makes the box **shrink to
 *   fit** the space remaining to the viewport edge. It does NOT spill. So an
 *   unclamped popover near the right edge silently COMPRESSES (measured:
 *   320px of content rendering at 203px against a right-hand column), and
 *   only actually clips once the space drops below its min-content width.
 *   Compression is the common failure; clipping is the rare one.
 * - Because of that compression, you cannot measure the popover at its
 *   provisional position and then clamp from the measurement — you'd be
 *   measuring the squeezed box and clamping from a width that's already
 *   wrong. Callers pass a KNOWN size instead.
 *
 * `maxHeight` comes back so the caller can constrain its content rather
 * than have the box clamped on top of the trigger. That matters for
 * hover-dismissed popovers specifically: a box under the cursor fires the
 * trigger's `mouseleave`, which unmounts it, which puts the cursor back on
 * the trigger, which reopens it — a flicker loop.
 */

export interface ResolvePopoverPositionArgs {
  /** The trigger's viewport rect. */
  anchor: DOMRect
  /**
   * The popover's size. Must be its UNCONSTRAINED size — a fixed width from
   * a utility class, and a height either fixed or derived from the content
   * (row count), never read back from a rendered-and-compressed box.
   */
  size: { width: number; height: number }
  /** Preferred side. Flips when the preferred side doesn't fit. Default `'below'`. */
  prefer?: 'above' | 'below'
  /**
   * Horizontal alignment against the trigger. `'start'` aligns the popover's
   * leading edge to the trigger's (default); `'end'` aligns trailing edges,
   * which is what a wide picker hanging off a small button wants.
   */
  align?: 'start' | 'end'
  /** Minimum gap to the viewport edge, and between popover and trigger. Default 8. */
  margin?: number
  /**
   * When true (default) and NEITHER side has room, the popover is capped to
   * the larger gap instead of being clamped over the trigger.
   */
  avoidAnchorOverlap?: boolean
}

export interface ResolvedPopoverPosition {
  top: number
  left: number
  /**
   * Height budget for the content. Equals the requested height when there
   * was room; smaller when the popover had to be squeezed into the
   * available gap. Apply it as a `max-height` with `overflow-y: auto`.
   */
  maxHeight: number
}

export function resolvePopoverPosition({
  anchor,
  size,
  prefer = 'below',
  align = 'start',
  margin = 8,
  avoidAnchorOverlap = true,
}: ResolvePopoverPositionArgs): ResolvedPopoverPosition {
  const vw = window.innerWidth
  const vh = window.innerHeight

  /* ------------------------------ vertical ------------------------------ */

  const roomBelow = vh - anchor.bottom - margin * 2
  const roomAbove = anchor.top - margin * 2
  const fitsBelow = size.height <= roomBelow
  const fitsAbove = size.height <= roomAbove

  let top = anchor.bottom + margin
  let maxHeight = size.height

  const placeBelow = () => {
    top = anchor.bottom + margin
  }
  const placeAbove = () => {
    top = anchor.top - margin - size.height
  }

  if (prefer === 'below' ? fitsBelow : fitsAbove) {
    // Preferred side has room.
    if (prefer === 'below') placeBelow()
    else placeAbove()
  } else if (prefer === 'below' ? fitsAbove : fitsBelow) {
    // Flip to the other side.
    if (prefer === 'below') placeAbove()
    else placeBelow()
  } else if (avoidAnchorOverlap) {
    // Neither side fits. Take the larger gap and SHRINK into it rather than
    // clamping over the trigger — see the flicker-loop note in the header.
    const useBelow = roomBelow >= roomAbove
    maxHeight = Math.max(0, useBelow ? roomBelow : roomAbove)
    top = useBelow ? anchor.bottom + margin : anchor.top - margin - maxHeight
  } else {
    // Overlap permitted: clamp to the viewport and accept covering the trigger.
    top = Math.max(margin, Math.min(vh - margin - size.height, anchor.bottom + margin))
  }

  const resolvedTop = Math.max(margin, top)

  /* ----------------------------- horizontal ----------------------------- */

  const desiredLeft = align === 'end' ? anchor.right - size.width : anchor.left
  // Clamp both edges. The `Math.max(margin, …)` runs LAST so a popover wider
  // than the viewport pins to the leading edge rather than going negative.
  const left = Math.max(margin, Math.min(vw - margin - size.width, desiredLeft))

  return { top: resolvedTop, left, maxHeight }
}
